import React, {
  useImperativeHandle,
  useState,
  useLayoutEffect,
  forwardRef,
  memo,
  useEffect,
} from 'react';
import useModalController from '../../hooks/useModalController';
import { useModalContext } from './ModalProvider';

const ModalControlled = forwardRef(
  (
    {
      Component,
      renderWhenOpen,
      mountDelayedTimeout = 500,
      type,
      modalKey,
      ...props
    },
    ref
  ) => {
    const [dialogProps, showModal] = useModalController(type);
    const [[mountDelayed, delayedProps], setMountDelayed] = useState([
      dialogProps.open,
      dialogProps,
    ]);
    const { markModalOpen, markModalClosed } = useModalContext();

    useLayoutEffect(() => {
      if (renderWhenOpen) {
        if (dialogProps.open === mountDelayed) {
          return;
        }

        if (dialogProps.open) {
          setMountDelayed([dialogProps.open, dialogProps]);
        }

        const timeoutId = setTimeout(() => {
          setMountDelayed([false, {}]);
        }, mountDelayedTimeout);

        return () => clearTimeout(timeoutId);
      }
      // don't add dialogProps to deps this done to trigger effect efficient
    }, [renderWhenOpen, dialogProps.open, mountDelayed, mountDelayedTimeout]);

    useImperativeHandle(
      ref,
      () => ({
        showModal,
        open: dialogProps.open,
        onConfirm: dialogProps.onConfirm,
        onClose: dialogProps.onClose,
      }),
      [dialogProps.onClose, dialogProps.onConfirm, dialogProps.open, showModal]
    );

    useEffect(() => {
      if (!modalKey) {
        return;
      }
      if (dialogProps.open) {
        markModalOpen(modalKey, ref);
      } else {
        markModalClosed(modalKey);
      }
      return () => {
        markModalClosed(modalKey);
      };
    }, [dialogProps.open, modalKey, ref]);

    if (renderWhenOpen) {
      return mountDelayed && dialogProps.open ? (
        <Component {...props} {...delayedProps} {...dialogProps} />
      ) : null;
    }

    return <Component {...props} {...dialogProps} />;
  }
);

ModalControlled.displayName = 'ModalControlled';

export default memo(ModalControlled);
